import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent } from 'react';

/**
 * A multi-value email field: one chip per address, a text box to add more.
 *
 * Pre-P8 punch list, Tier 2 (notification recipients UX): Company Settings
 * and each Location override used to edit `notification_recipients` as ONE
 * comma-separated free-text input — no per-address display, no way to remove
 * just one entry, and a typo was invisible until the database's own CHECK
 * refused the whole save with a constraint name. This is the input-UX fix
 * only: the parsing rule is unchanged (split, trim, lower-case), the value
 * handed back is still `string[]`, and who the list is FOR is deliberately
 * not revisited here (the P7 digest already pulls admins live; that precedent
 * is noted in PRE_P8_FINDINGS.md as a separate, bigger question).
 *
 * Behaviour, settled by three review lenses (UX, accessibility, state):
 *   - Enter or comma commits the typed text as a chip. Pasting "a@x, b@y"
 *     splits into several chips at once. Blur commits valid text too.
 *   - Tab is NEVER blocked (WCAG 2.1.2): with valid text it commits and
 *     moves on; with invalid text it moves on and the reason stays visible.
 *   - An address that fails the format check is not added; it stays in the
 *     box, the box is marked invalid, and a sentence under it says why. When
 *     several were typed or pasted, every GOOD one is added and every bad or
 *     duplicate one is left in the box together — nothing typed is lost.
 *   - Backspace on an empty box lifts the last chip back INTO the box as
 *     editable text rather than deleting it outright (a one-keystroke slip
 *     past your own text must not silently drop a real recipient). Each chip
 *     also has its own labelled Remove button, ≥44px tall on a phone.
 *   - The parent form MUST call `flush()` (via ref) before saving: it commits
 *     any pending text and returns the final list, or the problem sentence
 *     when the text cannot be added. Relying on blur alone let a Save with an
 *     invalid draft still in the box "succeed" while quietly dropping it —
 *     every lens flagged it.
 *
 * The format check MIRRORS the database's `recipients_look_like_emails`
 * constraint (migration 0004: `[^@,\s]+@[^@,\s]+\.[^@,\s]+`), so nothing this
 * control accepts can be refused server-side for format — and nothing it
 * refuses would have been accepted there. It is a shape check, not
 * deliverability: a mistyped but well-formed address still goes through,
 * exactly as before.
 */

const EMAIL_SHAPE = /^[^@,\s]+@[^@,\s]+\.[^@,\s]+$/;

export function looksLikeEmail(s: string): boolean {
  return EMAIL_SHAPE.test(s);
}

export type FlushResult = { ok: true; value: string[] } | { ok: false; problem: string };

export interface EmailChipsInputHandle {
  /** Commit any text still in the box. The returned list is what the form should save. */
  flush: () => FlushResult;
}

/** Lower-case and de-duplicate — the same normalisation the old comma-list parser applied on every save. */
function normalise(list: string[]): string[] {
  const out: string[] = [];
  for (const raw of list) {
    const v = raw.trim().toLowerCase();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

function tokenise(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

export const EmailChipsInput = forwardRef<
  EmailChipsInputHandle,
  {
    /** The id the surrounding <label htmlFor> points at (the text box). */
    id: string;
    value: string[];
    onChange: (next: string[]) => void;
    disabled?: boolean;
    /** Phone layout: 44px chip-remove targets. */
    narrow?: boolean;
    /** Extra ids to append to the text box's aria-describedby (e.g. the field's own hint). */
    describedBy?: string;
    /** Shown in the empty box. The two callers have opposite empty-state meanings, so each says its own. */
    placeholder?: string;
  }
>(function EmailChipsInput({ id, value, onChange, disabled, narrow, describedBy, placeholder }, ref) {
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [announce, setAnnounce] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const problemId = useId();

  // What is rendered and compared against. Legacy rows were saved without
  // de-duplication and with whatever case was typed before the parser
  // lower-cased them, so normalise on the way in rather than trusting `value`.
  const list = normalise(value);

  // A fresh `value` from OUTSIDE (the parent re-seeding after a refresh or a
  // stale-write conflict) invalidates any "already in the list" sentence. A
  // change this component emitted itself must not — commit() can add good
  // tokens and set a problem for the bad ones in the same breath, and the
  // problem must survive that onChange. `emittedKey` tells the two apart.
  const valueKey = list.join(',');
  const emittedKey = useRef(valueKey);
  useEffect(() => {
    if (emittedKey.current !== valueKey) setProblem(null);
    emittedKey.current = valueKey;
  }, [valueKey]);
  function emit(next: string[]) {
    emittedKey.current = next.join(',');
    onChange(next);
  }

  /**
   * Try to commit `raw` (possibly several comma/space-separated addresses).
   * Adds every acceptable token, leaves the rest in the box with one sentence
   * naming them, and returns the resulting list plus the problem (if any).
   */
  function commit(raw: string): { value: string[]; problem: string | null; added: string[] } {
    const tokens = tokenise(raw);
    const next = [...list];
    const added: string[] = [];
    const malformed: string[] = [];
    const duplicate: string[] = [];
    for (const t of tokens) {
      if (!looksLikeEmail(t)) malformed.push(t);
      else if (next.includes(t)) duplicate.push(t);
      else {
        next.push(t);
        added.push(t);
      }
    }
    if (added.length) emit(next);

    const leftover = [...malformed, ...duplicate];
    let sentence: string | null = null;
    if (leftover.length) {
      const parts: string[] = [];
      if (added.length) parts.push(added.length === 1 ? `Added ${added[0]}.` : `Added ${added.length} addresses.`);
      if (malformed.length === 1) {
        parts.push(`“${malformed[0]}” does not look like an email address — check it, then press Enter to add it or clear the box to move on.`);
      } else if (malformed.length > 1) {
        parts.push(`These do not look like email addresses: ${malformed.join(', ')}. Check them, then press Enter to add them or clear the box to move on.`);
      }
      if (duplicate.length) {
        parts.push(duplicate.length === 1 ? `${duplicate[0]} is already in the list.` : `Already in the list: ${duplicate.join(', ')}.`);
      }
      sentence = parts.join(' ');
    }

    setDraft(leftover.join(', '));
    setProblem(sentence);
    if (added.length) {
      // Announced even when something else was rejected — a sighted user sees
      // the chip appear; a screen-reader user otherwise hears only the alert.
      setAnnounce(added.length === 1 ? `Added ${added[0]}.` : `Added ${added.length} addresses.`);
    }
    return { value: next, problem: sentence, added };
  }

  useImperativeHandle(ref, () => ({
    flush: () => {
      if (draft.trim() === '') return { ok: true, value: list };
      const r = commit(draft);
      return r.problem ? { ok: false, problem: r.problem } : { ok: true, value: r.value };
    },
  }));

  function remove(addr: string, backToBox: boolean) {
    emit(list.filter((v) => v !== addr));
    setAnnounce(backToBox ? `${addr} moved back into the box for editing.` : `Removed ${addr}.`);
    if (backToBox) setDraft(addr);
    // Clear the problem sentence unless the box still holds the text it
    // describes: lifting a chip back replaces that text (caught live — an
    // "already in the list" line outlived the chip it was about).
    if (backToBox || draft.trim() === '') setProblem(null);
    // The chip's button unmounts itself — hand focus somewhere real rather
    // than letting it drop to <body> (the rule every removable control in
    // this app follows).
    inputRef.current?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
      return;
    }
    if (e.key === 'Tab') {
      // Commit what can be committed and let focus move either way.
      if (draft.trim() !== '') commit(draft);
      return;
    }
    if (e.key === 'Backspace' && draft === '' && list.length > 0) {
      e.preventDefault();
      const last = list[list.length - 1];
      if (last !== undefined) remove(last, true);
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    if (!/[,\s]/.test(text.trim())) return; // a single token: let the input take it
    e.preventDefault();
    commit(draft + ' ' + text);
  }

  const described = [hintId, problem ? problemId : null, describedBy ?? null].filter(Boolean).join(' ');
  const removeStyle = narrow ? { ...S.removeBtn, ...S.removeBtnNarrow } : S.removeBtn;

  return (
    <div style={S.wrap}>
      {list.length > 0 && (
        <ul aria-label="Recipients" style={S.list}>
          {list.map((addr) => (
            <li key={addr} style={narrow ? { ...S.chip, ...S.chipNarrow } : S.chip}>
              <span style={S.chipText}>{addr}</span>
              <button
                type="button"
                onClick={() => remove(addr, false)}
                disabled={disabled}
                aria-label={`Remove ${addr}`}
                style={removeStyle}
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        id={id}
        ref={inputRef}
        type="text"
        inputMode="email"
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={draft}
        disabled={disabled}
        placeholder={list.length === 0 ? (placeholder ?? 'name@example.com') : 'Add another…'}
        aria-invalid={problem ? true : undefined}
        aria-describedby={described}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => {
          if (!disabled && draft.trim() !== '') commit(draft);
        }}
        style={{ ...S.input, ...(problem ? S.inputInvalid : null) }}
      />
      <p id={hintId} style={S.hint}>
        Type an address and press Enter (or a comma) to add it. Remove one with its × button.
      </p>
      {problem && (
        <p id={problemId} role="alert" style={S.problem}>
          {problem}
        </p>
      )}
      {/* Add/remove confirmations for screen readers only; the chips themselves
          are the visual confirmation. Always mounted, so a change is announced. */}
      <span role="status" aria-live="polite" style={S.srOnly}>
        {announce}
      </span>
    </div>
  );
});

const S = {
  // position: relative so the absolutely-positioned announcer below can never
  // widen the page (item 7 of the overnight run caught exactly that, live).
  wrap: { position: 'relative', display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4rem',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.15rem',
    maxWidth: '100%',
    paddingLeft: '0.65rem',
    borderRadius: '999px',
    background: '#eef3fc',
    border: '1px solid #c9d8f5',
    color: '#0b2f7a', // 11.1:1 on #eef3fc
    fontSize: '0.88rem',
    lineHeight: 1.2,
  },
  chipNarrow: { fontSize: '1rem' },
  chipText: { overflowWrap: 'anywhere', padding: '0.3rem 0' },
  // A real button, not a decorative ×: 32px on desktop (WCAG 2.2 AA's 2.5.8
  // asks 24px; the 0.4rem chip gap keeps neighbours from touching), 44px on
  // a phone where the fat-finger risk is real.
  removeBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '2rem',
    minHeight: '2rem',
    padding: 0,
    border: 0,
    borderRadius: '999px',
    background: 'transparent',
    color: '#0b2f7a',
    fontSize: '1.15rem',
    lineHeight: 1,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  removeBtnNarrow: { minWidth: '2.75rem', minHeight: '2.75rem' },
  input: {
    padding: '0.45rem 0.6rem',
    borderRadius: '0.4rem',
    border: '1px solid #d5dbe5',
    // 16px: iOS Safari auto-zooms the page on focus of any smaller control
    // (pre-P8 punch list, Tier 2 — RequestTimeOff.tsx's S.input has the story).
    fontSize: '1rem',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  },
  // Full `border` shorthand (the base sets `border`, and React warns when a
  // shorthand and its longhand are mixed across a rerender).
  inputInvalid: { border: '1px solid #b00020', outlineColor: '#b00020' },
  hint: { margin: 0, fontSize: '0.78rem', color: '#666', lineHeight: 1.4 },
  problem: { margin: 0, fontSize: '0.82rem', color: '#b00020', lineHeight: 1.4 },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
} satisfies Record<string, React.CSSProperties>;
